import {
  COMPLETED_TASK_STATUS,
  DUE_SOON_TASK_STATUS,
  OVERDUE_TASK_STATUS,
  TO_DO_TASK_STATUS,
} from "@/constants/tasks";
import { format, isAfter } from "date-fns";
import { API_TBase } from "./API";
import { API_TUser } from "./auth";

const PRIORITY_MEDIUM = "medium";
const PRIORITY_HIGH = "high";

const DATETIME_FORMAT = "dd/MM/yyyy";

type API_TComplianceSection =
  | "company_info"
  | "company_documents"
  | "key_personnel_details"
  | "company_checklist"
  | "employees_info"
  | "employees_documents"
  | "timesheets"
  | "employees_checklists";

type TTaskType =
  | "flow"
  | "guide"
  | "document"
  | "info"
  | "report_employees_number_change"
  | "onboard_sponsored_employee"
  | "submit_for_review"
  | "onboard_candidate_assessment"
  | "right_to_work_check_employee"
  | "right_to_work_check_employer"
  | "guide_with_custom_button";

type TTaskStatus = "to_do" | "archived";

export type NullBool = {
  Bool: boolean;
  Valid: boolean;
};

export type API_TTask = {
  start_date: string;
  due_date: string;
  expiration_date: string;
  status: TTaskStatus;
  template_id: number;
  user_id: number;
  user: API_TUser;
  user_flow_ids: string;
  target_user: API_TUser;
  target_user_id: number;
  document_template_id: string;
  type: TTaskType;
  name: string;
  title: string;
  description: string;
  compliance_category: API_TComplianceSection;
  has_sms_action: boolean;
  sms_action_time: string;
  rule: string;
  rule_after_creation: string;
  priority: string;
  service: string;
  product_type: string;
  product_type_id: number;
  created_by_expert: NullBool;
  hide_button: boolean;
  document_template_v2: TDocument_Template_V2;
  document_template_v2_id: number;
  directory_id: number;
  file_id: number;
  company_id: number;
  button_name: string;
  button_url: string;
} & API_TBase;

export type TTask_User = {
  ID: number;
  role: string;
};

export type API_TTasks = {
  my_tasks?: API_TTask[];
  side_tasks?: API_TTask[];
  tasks?: API_TNewTask[];
};

export type TTask_Table = {
  id: number;
  name: string;
  type: TTaskType;
  status: TTaskStatus;
  priority: string;
  complianceSection: string;
  actionBy: string;
  dueDate: string;
  expDate: string;
  service: string;
  productType: string;
  productTypeId: number;
};

export type API_TNewTask = {
  id: number;
  task_name: string;
  status: string;
  compliance_section: string;
  action_by: string;
  start_date: string;
  due_date: string;
};

export type TNewTask_Table = {
  id: number;
  name: string;
  status: string;
  complianceSection: string;
  actionBy: string;
  dueDate: string;
  badgeType: string;
};

const replaceUnderscoreBySpace = (s: string) => s.replaceAll("_", " ");

const firstLetterToUpperCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const convertComplianceSection = (section: string) => {
  if (section === "employees_documents") return "Documents";
  if (section === "employees_info") return "Personal Info";
  if (section === "company_checklist") return "Checklist";
  if (section === "employees_checklists") return "Checklist";

  if (section === "employee_record_keeping_duties" || section === "employer_record_keeping_duties")
    return "Record-keeping duties";

  if (section === "employee_reporting_duties" || section === "employer_reporting_duties")
    return "Reporting duties";

  if (section === "employer_genuine_employment_duties") return "Genuine employment duties";

  if (section === "employer_onboarding") return "Onboarding";

  section = replaceUnderscoreBySpace(section);
  section = firstLetterToUpperCase(section);

  return section ? section : "―";
};

const convertNullBool = (section: NullBool) => (section.Valid ? section.Bool : false);

const convertTaskStatusToBadgeType = (status: string): string => {
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, "_");

  if (normalizedStatus === DUE_SOON_TASK_STATUS || status === "Due Soon" || status === "Due soon")
    return "VividOrange400";
  if (normalizedStatus === TO_DO_TASK_STATUS || status === "To do" || status === "To Do")
    return "DeepTeal800";
  if (normalizedStatus === COMPLETED_TASK_STATUS || status === "Completed") return "DeepTeal300";
  if (normalizedStatus === OVERDUE_TASK_STATUS || status === "Overdue") return "RaspberryPink400";

  return "LimeGreen100";
};

export const convertTaskForTable = (a: API_TTask): TTask_Table => {
  const formatStr = DATETIME_FORMAT;
  const dueDate = new Date(a.due_date);
  const expDate = new Date(a.expiration_date);

  const priority = a.priority || PRIORITY_MEDIUM;

  return {
    id: a.ID,
    name: a.name,
    type: a.type,
    status: a.status,
    priority: firstLetterToUpperCase(priority),
    complianceSection: convertComplianceSection(a.compliance_category),
    actionBy: `${a.user?.first_name} ${a.user?.last_name}`,
    dueDate: format(dueDate, formatStr),
    expDate: format(expDate, formatStr),
    service: a.service ? a.service : "―",
    productType: a.product_type,
    productTypeId: a.product_type_id,
  };
};

export const convertNewTaskForTable = (a: API_TNewTask): TNewTask_Table => {
  const formatStr = DATETIME_FORMAT;
  const dueDate = new Date(a.due_date);

  return {
    id: a.id,
    name: a.task_name,
    status: a.status,
    complianceSection: convertComplianceSection(a.compliance_section),
    actionBy: a.action_by,
    dueDate: format(dueDate, formatStr),
    badgeType: convertTaskStatusToBadgeType(a.status),
  };
};

type TTaskTableSort = (a: TTask_Table, b: TTask_Table) => -1 | 1 | 0;

type TTaskTableSortMap<T = TTaskTableSort> = {
  byDueDate: T;
  byPriority: T;
};

export const tableTasksSorts: TTaskTableSortMap = {
  byDueDate: (a, b) => {
    return isAfter(new Date(a.dueDate), new Date(b.dueDate)) ? -1 : 1;
  },

  byPriority: (a, b) => {
    if (a.priority === PRIORITY_HIGH && b.priority === PRIORITY_MEDIUM) return -1;

    if (a.priority === PRIORITY_MEDIUM && b.priority === PRIORITY_HIGH) return 1;

    return 0;
  },
};

type TDocument_Template_V2 = {
  ID: number;
  alias: string;
  case_template_id: number;
  checks: string;
  description: string;
  example_path: string;
  file_type: string;
  index: number;
  name: string;
  parent_alias: string;
  read_role_access: string;
  write_role_access: string;
};

export type TTask_Template = {
  id: number;
  status: TTaskStatus;
  type: TTaskType;
  name: string;
  title: string;
  description: string;
  user_id: number;
  user: API_TUser;
  target_user_id: number;
  target_user: API_TUser;
  user_role: string;
  product_type: string;
  product_type_id: number;
  created_by_expert: boolean;
  hide_button: boolean;
  document_template_v2: TDocument_Template_V2;
  document_template_v2_id: number;
  directory_id: number;
  file_id: number;
  company_id: number;
  button_name: string;
  button_url: string;
};

export const convertTaskForTemplate = (a: API_TTask): TTask_Template => ({
  id: a.ID,
  status: a.status,
  type: a.type,
  name: a.name,
  title: a.title,
  description: a.description,
  user_id: a.user_id,
  user: a.user,
  target_user_id: a.target_user_id,
  target_user: a.target_user,
  user_role: a.user.role,
  product_type: a.product_type,
  product_type_id: a.product_type_id,
  created_by_expert: convertNullBool(a.created_by_expert),
  hide_button: a.hide_button,
  document_template_v2: a.document_template_v2,
  document_template_v2_id: a.document_template_v2_id,
  directory_id: a.directory_id,
  file_id: a.file_id,
  company_id: a.company_id,
  button_name: a.button_name,
  button_url: a.button_url,
});

export type TActions = {
  delete?: Function;
  edit?: Function;
  download?: Function;
};

export type TTableHeader = {
  text: string;
  value: string;
  sortable: boolean;
  filterable: boolean;
  align: string;
  icon: string;
  error: string;
  slot: string;
};

export type TRule = {
  name?: string;
  column?: string;
  value?: string;
};
